import apiClient from "./apiClient";

/**
 * ONE grant, TWO products.
 *
 * Lead capture (`leadCapture`) and ads publishing (`adsManager`) are sold
 * separately but Meta issues a single authorisation covering both, so the
 * dialog has two entry points — Settings → Lead Sources and Marketing → Ad
 * Accounts — and an institute may be entitled to only one of them. Each entry
 * point has its own config endpoint because each is gated on its own plan
 * feature; both are projections of the same server-side builder, and the code
 * they produce is redeemed at the same single endpoint.
 *
 * Ordered by who is most likely to be asking, then tried in turn: the caller
 * knows which screen it is, but not which features the institute holds, and a
 * 403 from the first is an ordinary answer rather than a failure.
 */
const ENDPOINTS = {
  "lead-sources": [
    "/lead-sources/meta-ads/connect-config",
    "/marketing/connections/meta/business-config",
  ],
  marketing: [
    "/marketing/connections/meta/business-config",
    "/lead-sources/meta-ads/connect-config",
  ],
};

/**
 * The Meta app id, redirect URI, signed state and — the part that matters —
 * the scopes **this institute's plan entitles it to**. A lead-only school is
 * never shown an ads permission it cannot grant, and that decision is the
 * server's, not this file's.
 *
 * @param {object} [opts]
 * @param {'lead-sources'|'marketing'} [opts.from] - which screen is asking
 * @returns {Promise<object|null>} config, or null when no endpoint answered
 */
export async function fetchMetaBusinessConfig({ from = "lead-sources" } = {}) {
  for (const endpoint of ENDPOINTS[from] || ENDPOINTS["lead-sources"]) {
    try {
      const { data } = await apiClient.get(endpoint);
      if (data?.appId) return data;
    } catch {
      // Plan-locked or permission-locked for this user — try the other door.
    }
  }
  return null;
}

/**
 * Opens Meta's Pages / Lead Ads / Ads authorisation dialog.
 *
 * @param {object} opts
 * @param {'lead-sources'|'marketing'} opts.from
 * @returns {Promise<Window|null>} the popup, so the caller can watch it close.
 *   Throws with a user-presentable message when nothing is configured.
 */
export async function startMetaBusinessConnect({ from = "lead-sources" } = {}) {
  const data = await fetchMetaBusinessConfig({ from });
  if (!data) {
    throw new Error(
      "Facebook connection is not available on this plan, or the Meta app is not configured yet.",
    );
  }

  const scope = (data.businessScopes || []).join(",");
  const redirectUri = data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;

  // `auth_type=rerequest` is what makes this work as a *re*-connect: without it
  // Meta silently skips permissions the user declined before, so the dialog
  // closes "successfully" having granted nothing new.
  const url = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${data.appId}`
    + `&redirect_uri=${encodeURIComponent(redirectUri)}`
    + `&scope=${scope}&response_type=code&auth_type=rerequest`
    + `&state=${encodeURIComponent(data.businessState || "business")}`;

  return window.open(url, "meta_business_grant", "width=600,height=700,scrollbars=yes");
}
